export type ClimateZone = 'desert' | 'alpine' | 'tropical' | 'coastal' | 'highAltitude' | 'continental';
export type Season = 'winter' | 'spring' | 'summer' | 'fall';
export type PackingCondition = ClimateZone | Season;

interface BoundingBox {
  name: string;
  zone: ClimateZone;
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/**
 * Known regions for climate classification.
 * Order matters — first match wins. More specific regions should come first.
 */
const CLIMATE_REGIONS: BoundingBox[] = [
  // High altitude dry — most specific, check first
  { name: 'Atacama Desert', zone: 'highAltitude', minLat: -28, maxLat: -18, minLon: -71, maxLon: -67 },
  { name: 'Tibetan Plateau', zone: 'highAltitude', minLat: 27, maxLat: 40, minLon: 78, maxLon: 100 },
  { name: 'Altiplano', zone: 'highAltitude', minLat: -22, maxLat: -14, minLon: -70, maxLon: -64 },
  { name: 'Ethiopian Highlands', zone: 'highAltitude', minLat: 6, maxLat: 15, minLon: 35, maxLon: 45 },

  // Coastal regions — check before alpine/desert
  { name: 'Pacific Northwest', zone: 'coastal', minLat: 42, maxLat: 50, minLon: -125, maxLon: -121 },
  { name: 'Northern California Coast', zone: 'coastal', minLat: 37, maxLat: 42, minLon: -125, maxLon: -122 },
  { name: 'British Isles', zone: 'coastal', minLat: 50, maxLat: 60, minLon: -11, maxLon: 2 },
  { name: 'NZ Coast', zone: 'coastal', minLat: -47, maxLat: -34, minLon: 166, maxLon: 179 },
  { name: 'Patagonia Coast', zone: 'coastal', minLat: -55, maxLat: -40, minLon: -76, maxLon: -65 },

  // Desert regions — check before alpine so canyon country doesn't get classified as mountain
  { name: 'US Southwest', zone: 'desert', minLat: 31, maxLat: 37, minLon: -120, maxLon: -104 },
  { name: 'Utah Canyon Country', zone: 'desert', minLat: 37, maxLat: 40, minLon: -112, maxLon: -109 },
  { name: 'US Great Basin', zone: 'desert', minLat: 37, maxLat: 42, minLon: -120, maxLon: -112 },
  { name: 'Sahara', zone: 'desert', minLat: 15, maxLat: 35, minLon: -17, maxLon: 35 },
  { name: 'Arabian Desert', zone: 'desert', minLat: 15, maxLat: 32, minLon: 35, maxLon: 60 },
  { name: 'Australian Outback', zone: 'desert', minLat: -32, maxLat: -20, minLon: 120, maxLon: 145 },
  { name: 'Namib Desert', zone: 'desert', minLat: -28, maxLat: -17, minLon: 12, maxLon: 18 },
  { name: 'Thar Desert', zone: 'desert', minLat: 24, maxLat: 30, minLon: 68, maxLon: 76 },

  // Alpine / mountain regions
  { name: 'Rocky Mountains (US)', zone: 'alpine', minLat: 37, maxLat: 50, minLon: -111.5, maxLon: -103 },
  { name: 'Sierra Nevada (US)', zone: 'alpine', minLat: 36, maxLat: 40, minLon: -120, maxLon: -118 },
  { name: 'Cascade Range', zone: 'alpine', minLat: 40, maxLat: 49, minLon: -123, maxLon: -121 },
  { name: 'Alps', zone: 'alpine', minLat: 43, maxLat: 48, minLon: 5, maxLon: 17 },
  { name: 'Andes (Southern)', zone: 'alpine', minLat: -55, maxLat: -30, minLon: -73, maxLon: -67 },
  { name: 'Himalayas', zone: 'alpine', minLat: 26, maxLat: 36, minLon: 72, maxLon: 96 },
  { name: 'Scandinavian Mountains', zone: 'alpine', minLat: 60, maxLat: 71, minLon: 5, maxLon: 20 },
  { name: 'Southern Alps (NZ)', zone: 'alpine', minLat: -47, maxLat: -42, minLon: 167, maxLon: 174 },
];

/**
 * Classify climate zone from latitude/longitude.
 * Checks known bounding boxes first, then falls back to latitude-based heuristics.
 */
export function classifyClimate(lat: number, lon: number): ClimateZone {
  // Check known regions
  for (const region of CLIMATE_REGIONS) {
    if (lat >= region.minLat && lat <= region.maxLat && lon >= region.minLon && lon <= region.maxLon) {
      return region.zone;
    }
  }

  // Latitude-based fallbacks
  const absLat = Math.abs(lat);

  if (absLat < 23.5) return 'tropical';
  if (absLat > 65) return 'alpine'; // subarctic, similar gear needs

  return 'continental';
}

/**
 * Classify season from date and latitude (hemisphere-aware).
 */
export function classifySeason(dateStr: string, lat: number): Season {
  const month = new Date(dateStr).getMonth(); // 0-11
  const isNorthern = lat >= 0;

  // Northern hemisphere seasons
  const northernSeason: Season =
    month >= 2 && month <= 4 ? 'spring' :
    month >= 5 && month <= 7 ? 'summer' :
    month >= 8 && month <= 10 ? 'fall' :
    'winter';

  if (isNorthern) return northernSeason;

  // Southern hemisphere: flip
  const seasonFlip: Record<Season, Season> = {
    winter: 'summer',
    spring: 'fall',
    summer: 'winter',
    fall: 'spring',
  };

  return seasonFlip[northernSeason];
}

/**
 * Get the climate profile for an entire trip.
 * Returns the union of all climate zones and the dominant season.
 */
export function getTripClimateProfile(
  stops: Array<{ latitude: number; longitude: number; arrival_date: string }>
): { zones: ClimateZone[]; season: Season; conditions: PackingCondition[] } {
  if (stops.length === 0) {
    return { zones: ['continental'], season: 'summer', conditions: ['continental', 'summer'] };
  }

  const zones = new Set<ClimateZone>();
  const seasons: Season[] = [];

  for (const stop of stops) {
    zones.add(classifyClimate(stop.latitude, stop.longitude));
    seasons.push(classifySeason(stop.arrival_date, stop.latitude));
  }

  // Dominant season = most common
  const seasonCounts = seasons.reduce((acc, s) => {
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {} as Record<Season, number>);
  const dominantSeason = Object.entries(seasonCounts).sort((a, b) => b[1] - a[1])[0][0] as Season;

  const zoneArray = Array.from(zones);
  const conditions: PackingCondition[] = [...zoneArray, dominantSeason];

  return { zones: zoneArray, season: dominantSeason, conditions };
}
